/** Client-side PDF export (jsPDF + jspdf-autotable). */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { embedNotoSansForPdf, PDF_UNICODE_FAMILY } from '../pdfUnicodeFont';
import {
  sourceLabelFromResult,
  titleFromResult,
  normalizeUrl,
  digitsOnly,
  inferCarrierFromPrefix,
  cleanLogText,
  hasSecurityEscalation,
  extractPdfSourceTags,
  formatPdfSourceCell,
  formatPdfTitleCell,
  formatPdfTitleBlock,
  resolvePdfUrlForRow,
} from './resultFormatters.js';
import { collectDetailEntries, formatDetailScalar } from '../../utils/feedInspectDetails.js';
import { buildCyberTracerExportFilename } from './downloadUtils.js';

/** Body / sans text (Noto Sans when embedded, else Helvetica). */
let PDF_BODY_FONT = 'helvetica';
/** Log column: same as body when Unicode font loads (Courier has no RO diacritics). */
let PDF_MONO_FONT = 'courier';

const MARGIN = 14;
const PAGE_W_A4 = 210;
/** Brand / executive summary palette */
const COL_GREEN = [16, 185, 129]; // #10b981
const COL_BLUE = [59, 130, 246]; // #3b82f6
const COL_AMBER = [245, 158, 11]; // #f59e0b
const COL_SLATE = [71, 85, 105]; // #475569
/** Scam / spoofing emphasis (matches UI alert styling) */
const COL_ALERT = [225, 29, 72]; // #e11d48
const SECTION_GAP_MM = 12;
/** Space between Intelligence results table and the international cross-reference note */
const INTELLIGENCE_NOTE_GAP_MM = 6;
/** Every-page disclaimer (English, legal-style brevity). */
const PDF_OSINT_FOOTER =
  'OSINT report from public sources. Temporary server caches may hold investigation metadata; see Privacy Policy. Not legal advice.';

function formatInspectAttributeValue(entry) {
  if (!entry) return '';
  if (entry.kind === 'scalar') return formatDetailScalar(entry.value);
  if (entry.kind === 'array') {
    return (Array.isArray(entry.value) ? entry.value : [])
      .map((v) => (typeof v === 'object' && v != null ? JSON.stringify(v) : formatDetailScalar(v)))
      .filter(Boolean)
      .join('; ');
  }
  try {
    return JSON.stringify(entry.value);
  } catch {
    return String(entry.value ?? '');
  }
}

/**
 * Build Inspect-modal equivalent payload for one intelligence row (PDF Inspection Evidence).
 *
 * @param {object} res
 * @returns {{ title: string, source: string, deepSummary: string, attributes: Array<{label: string, value: string}>, evidence: string, url: string, timestamp: string }}
 */
function buildInspectionEvidenceForRow(res) {
  const title = titleFromResult(res) || sourceLabelFromResult(res) || 'Record';
  const source = sourceLabelFromResult(res) || String(res?.source || res?.platform || '—');
  const deepSummary = String(res?.snippet || res?.summary || res?.details?.summary || '').trim();
  const attrs = collectDetailEntries(res?.details || {})
    .slice(0, 24)
    .map((e) => ({
      label: e.label,
      value: formatInspectAttributeValue(e).slice(0, 500),
    }))
    .filter((a) => a.value);
  const url = normalizeUrl(resolvePdfUrlForRow(res) || res?.url || '') || String(res?.url || '').trim();
  const timestamp = formatDetailScalar(
    res?.details?.captured_at ||
      res?.details?.timestamp ||
      res?.details?.last_seen ||
      res?.timestamp ||
      '',
  );
  const evidenceParts = [];
  if (deepSummary) evidenceParts.push(deepSummary);
  if (timestamp) evidenceParts.push(`Captured: ${timestamp}`);
  if (url) evidenceParts.push(`Source: ${url}`);
  return {
    title: String(title).slice(0, 200),
    source: String(source).slice(0, 80),
    deepSummary: deepSummary.slice(0, 1200),
    attributes: attrs,
    evidence: evidenceParts.join('\n').slice(0, 1600),
    url,
    timestamp,
  };
}

/**
 * Draw Inspection Evidence blocks (Inspect modal parity) for selected results.
 *
 * @returns {number} final Y
 */
function drawInspectionEvidenceSection(doc, startY, results, pageW, pageH) {
  let y = startY;
  const list = (Array.isArray(results) ? results : []).filter((r) => r && typeof r === 'object');
  if (!list.length) return y;

  const ensureSpace = (needMm) => {
    if (y + needMm > pageH - MARGIN) {
      doc.addPage();
      y = MARGIN + 4;
    }
  };

  ensureSpace(16);
  doc.setFont(PDF_BODY_FONT, 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('INSPECTION EVIDENCE', MARGIN, y);
  y += 6;
  doc.setFont(PDF_BODY_FONT, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  const intro = doc.splitTextToSize(
    'Deep Summary, Extracted Entity Attributes, and Evidence Context for each selected record (same fields as INSPECT).',
    pageW - 2 * MARGIN,
  );
  doc.text(intro, MARGIN, y);
  y += intro.length * 3.6 + 4;

  list.forEach((res, idx) => {
    const block = buildInspectionEvidenceForRow(res);
    ensureSpace(28);
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(MARGIN, y - 3, pageW - 2 * MARGIN, 7, 1, 1, 'F');
    doc.setFont(PDF_BODY_FONT, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    const head = doc.splitTextToSize(
      `${idx + 1}. ${block.title} · ${block.source}`,
      pageW - 2 * MARGIN - 4,
    );
    doc.text(head, MARGIN + 2, y + 1.5);
    y += Math.max(8, head.length * 4 + 3);

    doc.setFont(PDF_BODY_FONT, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(30, 41, 59);
    doc.text('Deep Summary', MARGIN, y);
    y += 4;
    doc.setFont(PDF_BODY_FONT, 'normal');
    doc.setTextColor(51, 65, 85);
    const summaryLines = doc.splitTextToSize(block.deepSummary || 'No summary available.', pageW - 2 * MARGIN);
    ensureSpace(summaryLines.length * 3.5 + 4);
    doc.text(summaryLines, MARGIN, y);
    y += summaryLines.length * 3.5 + 3;

    if (block.attributes.length) {
      doc.setFont(PDF_BODY_FONT, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Extracted Entity Attributes', MARGIN, y);
      y += 4;
      const attrRows = block.attributes.map((a) => [a.label, a.value]);
      ensureSpace(12);
      autoTable(doc, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Attribute', 'Value']],
        body: attrRows,
        styles: {
          font: PDF_BODY_FONT,
          fontSize: 7.5,
          cellPadding: 1.8,
          overflow: 'linebreak',
          lineColor: [226, 232, 240],
        },
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 42, fontStyle: 'bold' },
          1: { cellWidth: 'auto' },
        },
      });
      y = doc.lastAutoTable && doc.lastAutoTable.finalY != null ? doc.lastAutoTable.finalY + 3 : y + 16;
    }

    // Unify when Deep Summary already equals Evidence Context body (avoid duplicate blocks).
    const evidenceBody = String(block.evidence || '')
      .replace(/^Captured:.*$/gim, '')
      .replace(/^Source:.*$/gim, '')
      .trim();
    const deepNorm = String(block.deepSummary || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const evidenceNorm = evidenceBody.replace(/\s+/g, ' ').trim().toLowerCase();
    const evidenceDuplicatesDeep =
      Boolean(deepNorm) && Boolean(evidenceNorm) && deepNorm === evidenceNorm;

    if (!evidenceDuplicatesDeep) {
      doc.setFont(PDF_BODY_FONT, 'bold');
      doc.setFontSize(8);
      doc.setTextColor(30, 41, 59);
      ensureSpace(10);
      doc.text('Evidence Context', MARGIN, y);
      y += 4;
      doc.setFont(PDF_BODY_FONT, 'normal');
      doc.setTextColor(51, 65, 85);
      const evLines = doc.splitTextToSize(block.evidence || 'No evidence context.', pageW - 2 * MARGIN);
      ensureSpace(evLines.length * 3.5 + 6);
      doc.text(evLines, MARGIN, y);
      y += evLines.length * 3.5;
    } else if (block.timestamp || block.url) {
      doc.setFont(PDF_BODY_FONT, 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      const metaParts = [];
      if (block.timestamp) metaParts.push(`Captured: ${block.timestamp}`);
      if (block.url) metaParts.push(`Source: ${block.url}`);
      if (metaParts.length) {
        const metaLines = doc.splitTextToSize(metaParts.join(' · '), pageW - 2 * MARGIN);
        ensureSpace(metaLines.length * 3.5 + 4);
        doc.text(metaLines, MARGIN, y);
        y += metaLines.length * 3.5;
      }
    }
    if (block.url && /^https?:\/\//i.test(block.url)) {
      y += 2;
      doc.setTextColor(...COL_BLUE);
      doc.setFont(PDF_BODY_FONT, 'normal');
      const linkLabel = doc.splitTextToSize(`External source: ${block.url}`, pageW - 2 * MARGIN);
      doc.textWithLink(linkLabel[0] || block.url, MARGIN, y, { url: block.url });
      y += Math.max(4, linkLabel.length * 3.5);
    }
    y += SECTION_GAP_MM * 0.55;
  });

  return y;
}

function isEmailPdfModule(moduleName) {
  return String(moduleName || '').toLowerCase() === 'email';
}

function isCompanyPdfModule(moduleName) {
  return String(moduleName || '').toLowerCase() === 'company';
}

function isPersonPdfModule(moduleName) {
  const m = String(moduleName || '').toLowerCase();
  return m === 'person' || m === 'name' || m === 'people';
}

/**
 * Due Diligence (company) PDF: merge stream logs with any confidence rows that landed only in `results`
 * so `[CONF:company_due_diligence] â€¦` lines (e.g. negative_news_ro rejected) appear in Technical Audit Trail.
 */
function mergeCompanyConfidenceLogEntries(logs, results, moduleName) {
  if (!isCompanyPdfModule(moduleName)) {
    return Array.isArray(logs) ? [...logs] : [];
  }
  const merged = [];
  const seen = new Set();
  const push = (e) => {
    if (!e || typeof e !== 'object') return;
    if (e.type !== 'log' && String(e.log_kind || '') !== 'confidence_breakdown') return;
    const msg = String(e.message || '').trim();
    if (!msg) return;
    const ts = e.timestamp != null ? String(e.timestamp) : '';
    const k = `${ts}|${msg}`;
    if (seen.has(k)) return;
    seen.add(k);
    merged.push(e);
  };
  (Array.isArray(logs) ? logs : []).forEach(push);
  (Array.isArray(results) ? results : []).forEach((e) => {
    if (!e || typeof e !== 'object') return;
    if (!/\[CONF:company_due_diligence\]/i.test(String(e.message || ''))) return;
    push(e);
  });
  return merged;
}

/**
 * Break long http(s) URLs onto multiple lines for jspdf-autotable (email PDF only).
 * Inserts \\n after slashes where valid â€” avoids ZWSP + monospace issues that caused stretched glyphs.
 */
function breakUrlForPdfTableCell(url, maxLineChars = 50) {
  const u = String(url || '').trim();
  if (!u || u === 'â€”' || !/^https?:\/\//i.test(u)) return u;
  if (u.length <= maxLineChars) return u;

  const lines = [];
  let remaining = u;
  const minSplit = 12;

  while (remaining.length > maxLineChars) {
    const window = remaining.slice(0, maxLineChars);
    const lastSlash = window.lastIndexOf('/');
    let cut = maxLineChars;
    if (lastSlash >= minSplit) cut = lastSlash + 1;
    lines.push(remaining.slice(0, cut).trimEnd());
    remaining = remaining.slice(cut);
  }
  if (remaining) lines.push(remaining);
  return lines.join('\n');
}

function truncatePdfPlainText(s, maxLen = 560) {
  const t = String(s || '').trim();
  if (!t) return '';
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(1, maxLen - 1))}â€¦`;
}

/**
 * Title column for PDF: keep one logical hit per table row; cap length; add a short snippet line for web hits.
 */
function pdfPrimaryTitle(res) {
  const base = titleFromResult(res);
  const sn = String(res?.snippet || '')
    .trim()
    .replace(/\s+/g, ' ');
  const isWebHit =
    res?.source === 'google_dork' ||
    /serper|brave|duckduckgo|search engine|web mention/i.test(
      `${res?.source || ''} ${res?.platform || ''} ${res?.type || ''}`
    );
  if (isWebHit && sn) {
    const tb = truncatePdfPlainText(base === 'â€”' ? '' : base, 220);
    const snip = truncatePdfPlainText(sn, 340);
    if (tb) return truncatePdfPlainText(`${tb}\n${snip}`, 620);
    return truncatePdfPlainText(snip, 620);
  }
  return truncatePdfPlainText(base, 620);
}

/**
 * If upstream accidentally concatenated several URLs into one `url` string, split into one PDF row each.
 * Also unwrap a single nested `results[]` payload if present.
 */
function flattenPdfIntelRowsForTable(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const out = [];
  for (const r of list) {
    if (!r || typeof r !== 'object') continue;
    if (Array.isArray(r.results) && r.results.length) {
      for (const inner of r.results) {
        if (inner && typeof inner === 'object') out.push(inner);
      }
      continue;
    }
    const rawUrl = normalizeUrl(r.url);
    const multi = extractMultipleHttpsUrls(rawUrl);
    if (multi && multi.length > 1) {
      const baseTitle = String(r.title || 'Web mention').trim() || 'Web mention';
      multi.forEach((u, i) => {
        out.push({
          ...r,
          url: u,
          title: `${baseTitle} (${i + 1}/${multi.length})`,
        });
      });
      continue;
    }
    out.push(r);
  }
  return out;
}

function extractMultipleHttpsUrls(rawUrl) {
  const raw = String(rawUrl || '').trim();
  if (!raw) return null;
  const m = raw.match(/https?:\/\/[^\s|,\n]+/gi);
  if (!m || m.length <= 1) return null;
  const uniq = [];
  const seen = new Set();
  for (const x of m) {
    const u = normalizeUrl(x);
    if (!u || seen.has(u)) continue;
    seen.add(u);
    uniq.push(u);
  }
  return uniq.length > 1 ? uniq : null;
}

function mergePdfRowTextSignals(res) {
  if (!res || typeof res !== 'object') return '';
  const parts = [
    res.title,
    res.snippet,
    res.platform,
    res.message,
    res.source_display_name,
    res.details?.name,
    res.details?.title,
    res.details?.misc,
    res.details?.extra_info,
  ];
  return parts
    .map((x) => String(x || '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function hasPdfHttpUrl(res) {
  const u = normalizeUrl(res?.url);
  return Boolean(u && /^https?:\/\//i.test(u));
}

function hasPdfNumericScoreLike(res) {
  const s = res?.score;
  if (s !== undefined && s !== null && s !== '' && !Number.isNaN(Number(s))) return true;
  const rs = res?.risk_score ?? res?.details?.risk_score;
  if (rs !== undefined && rs !== null && rs !== '' && !Number.isNaN(Number(rs))) return true;
  return false;
}

/**
 * Drop hollow rows for PDF tables (no URL, no substantive text, no score signal, no structured bucket).
 * Does not change in-app feed logic â€” export-only filter.
 */
function shouldRenderIntelRowInPdf(res) {
  if (!res || typeof res !== 'object') return false;

  if (res.is_hidden === true) return false;
  if (res.validation_status != null && res.validation_status !== 'passed') return false;
  if (res.exclude_from_export === true) return false;

  if (res.ui_bucket === 'cyber_trust_score' || res.ui_bucket === 'threat_intelligence') return true;
  if (res.ui_bucket === 'wayback_archive') return true;
  if (res.ui_bucket === 'email_deliverability' || res.ui_bucket === 'registered_service') return true;
  if (res.ui_bucket === 'ghunt_footprint') return true;
  if (res.source === 'hibp_status' || res.source === 'phone_metadata') return true;
  if (res.type === 'phone_identity') return true;

  if (hasPdfHttpUrl(res)) return true;

  const text = mergePdfRowTextSignals(res);
  if (text.length < 2) return false;

  if (hasPdfNumericScoreLike(res)) return true;
  if (Array.isArray(res.matched_tags) && res.matched_tags.length > 0) return true;
  if (res.details?.gaia_id != null && String(res.details.gaia_id).trim()) return true;

  if (res.source === 'hibp') {
    const dc = res.details?.dataclasses ?? res.details?.DataClasses;
    if (Array.isArray(dc) && dc.length > 0) return true;
    return text.length >= 4;
  }

  if (res.source === 'ghunt') {
    return text.length >= 4 || Boolean(res.details && Object.keys(res.details).length > 0);
  }

  if (text.length >= 12) return true;

  return false;
}

/** Strip tooling artefacts like "Email Available.json" from PDF title cells. */
function sanitizePdfIntelTitle(rawTitle) {
  let t = String(rawTitle || '').trim();
  if (!t) return t;
  t = t.replace(/\s*[-â€“â€”|]\s*[^\n]*?\.json\b/gi, '').trim();
  t = t.replace(/\b[\w .-]+\.json\b/gi, '').trim();
  t = t.replace(/\s{2,}/g, ' ').trim();
  return t || String(rawTitle || '').trim();
}

function isLongGoogleCdnUrl(u) {
  return /googleusercontent\.com|ggpht\.com|googleapis\.com\/usercontent/i.test(String(u || ''));
}

/** Gaia / Maps contrib id for GHunt rows (details or Maps URL). */
function extractGaiaIdFromResult(res) {
  if (!res || typeof res !== 'object') return '';
  if (res.gaia_id != null && String(res.gaia_id).trim()) return String(res.gaia_id).trim();
  const d = res.details;
  if (d && d.gaia_id != null) {
    const g = String(d.gaia_id).trim();
    if (g) return g;
  }
  const candidates = [
    res.url,
    res.maps_url,
    d?.url,
    d?.maps_url,
  ].filter(Boolean).map((x) => String(x).trim());
  for (const u of candidates) {
    const m = u.match(/google\.com\/maps\/contrib\/(\d{10,})/i);
    if (m) return m[1];
  }
  return '';
}

function appendGoogleUserIdToTitle(title, gaia) {
  if (!gaia) return String(title || '').trim();
  const t = String(title || '').trim();
  if (/google\s+user\s+id\s*:/i.test(t)) return t;
  const line = `Google User ID: ${gaia}`;
  return t ? `${t}\n${line}` : line;
}

function drawPdfFooterOnAllPages(doc, pageW, pageH) {
  const total = doc.getNumberOfPages();
  const maxW = pageW - 2 * MARGIN;
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setFont(PDF_BODY_FONT, 'italic');
    doc.setFontSize(7);
    doc.setTextColor(120, 130, 150);
    const lines = doc.splitTextToSize(PDF_OSINT_FOOTER, maxW);
    const lineH = 3.1;
    let y = pageH - MARGIN - lines.length * lineH + 2;
    if (y < MARGIN) y = pageH - MARGIN - 4;
    doc.text(lines, MARGIN, y);
  }
}

function hibpSecurityBlob(res) {
  if (!res || typeof res !== 'object') return '';
  const name = String(res.details?.name || res.platform || '').toLowerCase();
  const classes = (res.details?.dataclasses || res.details?.DataClasses || []).map((c) =>
    String(c).toLowerCase()
  );
  return [name, ...classes].join(' ');
}

/** Security-first tiering for Email PDF breach tables. */
function hibpSecurityTier(res) {
  const b = hibpSecurityBlob(res);
  if (
    /stealer|stealerlog|combolist|combo.list|combo.lists|cit0day|citoday|combo[-\s]?list/i.test(b)
  ) {
    return 'high';
  }
  return 'low';
}

function isStealerMalwareHibpRow(res) {
  const b = hibpSecurityBlob(res);
  return /stealer|stealerlog|stealer\s*logs/i.test(b);
}

function hibpRowsStealerRisk(hibpBreaches) {
  if (!Array.isArray(hibpBreaches)) return false;
  for (const r of hibpBreaches) {
    if (!r || typeof r !== 'object') continue;
    const name = String(r.details?.name || r.platform || '').toLowerCase();
    if (name.includes('stealer')) return true;
    const classes = r.details?.dataclasses ?? r.details?.DataClasses;
    if (!Array.isArray(classes)) continue;
    for (const c of classes) {
      if (/stealer/i.test(String(c))) return true;
    }
  }
  return false;
}

function githubLoginToDisplayName(login) {
  const s = String(login || '').trim();
  if (!s) return '';
  const parts = s.split(/[_-]+/).filter((p) => p.length >= 2 && /[a-zA-Z]/.test(p));
  if (parts.length < 2) return '';
  return parts.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

/** Human-readable label from Blackbird profile URLs (e.g. path slug â†’ display name). */
function extractBlackbirdDisplayIdentity(results) {
  const list = Array.isArray(results) ? results : [];
  let best = '';
  for (const r of list) {
    if (!r || r.source !== 'blackbird') continue;
    const url = String(r.url || '').trim();
    if (!url.startsWith('http')) continue;
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, '');
      const parts = u.pathname.split('/').filter(Boolean);
      const last = parts.length ? parts[parts.length - 1] : '';
      const slug = last.replace(/\.(html?|php|aspx)$/i, '');
      if (!slug || slug.length < 3) continue;
      const words = slug
        .replace(/[-_]+/g, ' ')
        .replace(/\d+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0);
      if (!words.length) continue;
      const human = words
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
      const label = `${human} (${host})`;
      if (label.length > best.length) best = label;
    } catch (_) {
      /* ignore */
    }
  }
  return best;
}

function extractProcurementPersonName(blob) {
  const text = String(blob || '').replace(/\s+/g, ' ');
  if (!text) return '';
  const junk = new Set(
    [
      'Notificare De',
      'Cumpărarea Directa',
      'Cumpararea Directa',
      'Servicii Medicale',
      'Award Notice',
      'Directa',
      'Google Search',
      'Facebook',
      'Website',
    ].map((x) => x.toLowerCase())
  );
  const re = /\b([A-Z\u00C0-\u024F][\w\u00E0-\u024F-]*(?:\s+[A-Z\u00C0-\u024F][\w\u00E0-\u024F-]*){1,2})\b/gu;
  let m;
  while ((m = re.exec(text)) !== null) {
    const cand = m[1].trim();
    const low = cand.toLowerCase();
    if (junk.has(low)) continue;
    const bits = cand.split(/\s+/);
    if (bits.length >= 2 && bits.every((w) => w.length >= 2)) return cand;
  }
  return '';
}

/**
 * Names for Email PDF executive block: Ghunt â†’ GitHub login/handle â†’ SEAP/dork â†’ local-part heuristic.
 */
function extractEmailIdentityCandidate(results, targetEmail) {
  const list = Array.isArray(results) ? results : [];
  const ghuntProfile = list.find(
    (r) => r && r.source === 'ghunt' && r.platform === 'Google Account Info' && r.details?.name
  );
  if (ghuntProfile?.details?.name) {
    const n = String(ghuntProfile.details.name).trim();
    if (n && !/^unknown$/i.test(n)) return n;
  }

  const ghRow = list.find((r) => r && r.source === 'github');
  if (ghRow?.details?.username) {
    const uname = String(ghRow.details.username).trim();
    if (uname) {
      const human = githubLoginToDisplayName(uname);
      if (human) return `${human} / ${uname}`;
      return uname;
    }
  }

  const blackbirdLabel = extractBlackbirdDisplayIdentity(list);
  if (blackbirdLabel) return `${blackbirdLabel} (Blackbird)`;

  let seapBlob = '';
  for (const r of list) {
    if (!r || r.source !== 'google_dork') continue;
    const blob = `${r.url || ''} ${r.title || ''} ${r.snippet || ''}`;
    if (/e-licitatie\.ro|seap|licitat|achiziÈ›|achizit/i.test(blob)) {
      seapBlob += ` ${r.title || ''} ${r.snippet || ''}`;
    }
  }
  const seapName = extractProcurementPersonName(seapBlob);
  if (seapName) return `${seapName} (public procurement / SEAP context)`;

  for (const r of list) {
    if (!r || r.source !== 'google_dork') continue;
    const n = extractProcurementPersonName(`${r.title || ''} ${r.snippet || ''}`);
    if (n) return n;
  }

  const fromLocal = emailNameCandidate({ emails: [String(targetEmail || '').trim()] });
  if (fromLocal) return `${fromLocal} (inferred from email local-part)`;

  return '';
}

const FOOTPRINT_HOST_RULES = [
  [/github\.com/i, 'GitHub'],
  [/twitter\.com/i, 'Twitter'],
  [/x\.com/i, 'X'],
  [/linkedin\.com/i, 'LinkedIn'],
  [/chess\.com/i, 'Chess.com'],
  [/stackoverflow\.com/i, 'Stack Overflow'],
  [/instagram\.com/i, 'Instagram'],
  [/facebook\.com|fb\.com/i, 'Facebook'],
];

const GITHUB_RESERVED = new Set([
  'features',
  'topics',
  'settings',
  'login',
  'signup',
  'search',
  'explore',
  'marketplace',
  'pulls',
  'issues',
]);

/**
 * Prefer canonical profile URLs over search/API endpoints (per platform).
 */
function scoreFootprintProfileUrl(label, urlStr) {
  let s = 0;
  let u;
  try {
    u = new URL(urlStr);
  } catch {
    return 0;
  }
  const path = u.pathname || '/';
  const segs = path.split('/').filter(Boolean);
  const lowPath = path.toLowerCase();
  const q = (u.search || '').toLowerCase();

  if (label === 'GitHub') {
    if (/\/search\b/i.test(path)) s -= 50;
    if (segs.length === 1 && !GITHUB_RESERVED.has(segs[0].toLowerCase())) s += 100;
    else if (segs.length >= 2 && !GITHUB_RESERVED.has(segs[0].toLowerCase())) s += 70;
    else s += 20;
  } else if (label === 'Twitter' || label === 'X') {
    if (/\/search\b/i.test(path) || /[?&]q=/.test(q) || /twitter\.com\/intent\//i.test(urlStr)) s -= 45;
    if (/intent\/user|\/i\/user\//i.test(path)) s += 92;
    if (segs.length === 1 && /^[a-z0-9_]{1,32}$/i.test(segs[0]) && !['search', 'home', 'explore', 'settings'].includes(segs[0].toLowerCase())) {
      s += 88;
    }
    s += 5;
  } else if (label === 'LinkedIn') {
    if (/\/in\//i.test(path)) s += 95;
    else if (/\/pub\/|\/dir\//i.test(path)) s += 35;
    else s += 25;
  } else if (label === 'Instagram') {
    if (segs.length === 1 && !['explore', 'accounts', 'reels', 'p'].includes(segs[0].toLowerCase())) s += 90;
    else s += 30;
  } else if (label === 'Facebook') {
    if (/profile\.php\?id=/i.test(urlStr) || (segs.length === 1 && segs[0].toLowerCase() !== 'search')) s += 85;
    else s += 25;
  } else if (label === 'Stack Overflow') {
    if (/\/users\/\d+/i.test(path)) s += 90;
    else s += 40;
  } else if (label === 'Chess.com') {
    if (/\/member\//i.test(path) || (segs.length >= 2 && segs[0].toLowerCase() === 'member')) s += 88;
    else s += 35;
  } else {
    s += 40;
  }

  if (/api\.|graphql/i.test(lowPath)) s -= 25;
  return s;
}

/** One profile URL per platform (best-effort relevance ranking). */
function collectDigitalFootprintProfiles(results) {
  const list = Array.isArray(results) ? results : [];
  /** @type {Map<string, Array<{ url: string, score: number }>>} */
  const byLabel = new Map();
  for (const r of list) {
    if (!r || typeof r !== 'object') continue;
    const raw = normalizeUrl(r.url);
    if (!raw || !/^https?:\/\//i.test(raw)) continue;
    for (const [re, label] of FOOTPRINT_HOST_RULES) {
      if (!re.test(raw)) continue;
      const score = scoreFootprintProfileUrl(label, raw);
      const bucket = byLabel.get(label) || [];
      if (bucket.some((x) => x.url === raw)) break;
      bucket.push({ url: raw, score });
      byLabel.set(label, bucket);
      break;
    }
  }
  const order = FOOTPRINT_HOST_RULES.map(([, lab]) => lab);
  const out = [];
  for (const label of order) {
    const bucket = byLabel.get(label);
    if (!bucket?.length) continue;
    bucket.sort((a, b) => b.score - a.score || b.url.length - a.url.length);
    out.push({ label, url: bucket[0].url });
  }
  return out;
}

function isGhuntGoogleAccountInfoRow(res) {
  return Boolean(
    res &&
      (res.ui_bucket === 'ghunt_footprint' ||
        (res.source === 'ghunt' && /google\s+account\s+info/i.test(String(res.platform || ''))))
  );
}

/** Line 1: optional GHunt display name; line 2: Gaia digits only (no mid-ID wraps). */
function formatGoogleGaiaPdfCell(gaia, displayName) {
  const idLine = String(gaia || '').replace(/\s+/g, '').trim();
  if (!idLine) return 'â€”';
  const name = String(displayName || '').trim();
  if (name && !/^unknown$/i.test(name)) {
    return `${sanitizePdfIntelTitle(name)}\n${idLine}`;
  }
  return idLine;
}

function computeEmailConfidenceScore(results) {
  const list = Array.isArray(results) ? results : [];
  const footprintN = collectDigitalFootprintProfiles(list).length;
  const hibpAll = list.filter((r) => r && r.source === 'hibp');
  const high = hibpAll.filter((r) => hibpSecurityTier(r) === 'high');

  if (footprintN >= 4) {
    return {
      level: 'HIGH',
      body: `Consolidated public footprint spans ${footprintN} major platform(s) in this export (strong cross-surface presence).`,
    };
  }
  if (hibpAll.length > 0 && high.length === 0 && footprintN === 0) {
    return {
      level: 'LOW',
      body:
        'Signals are dominated by historical / lower-immediate-risk breach listings; no consolidated social profile grid was produced in this export.',
    };
  }
  return {
    level: 'MEDIUM',
    body:
      'Mixed breach history and limited-to-moderate public footprint as captured in this pass (review detailed rows).',
  };
}

/**
 * Append Censys structured audit lines (CVE hints, TLS legacy, cert expiry) into the PDF title cell.
 */
function appendCensysPdfDetailLines(res, baseTit) {
  if (String(res?.platform || '').toLowerCase() !== 'censys') return baseTit;
  const lines = [];
  const hints = res.details?.software_cve_hints;
  if (Array.isArray(hints) && hints.length) {
    for (const h of hints.slice(0, 6)) {
      const cves = Array.isArray(h.cves) ? h.cves.join(', ') : '';
      if (cves) {
        lines.push(
          `Server may run ${String(h.product || 'service').trim()} ${String(h.version || '').trim()} â€” verify patch level vs ${cves}`
        );
      }
    }
  }
  const tls = res.details?.tls_compliance;
  if (tls?.legacy_protocol_risk && Array.isArray(tls.legacy_protocol_notes) && tls.legacy_protocol_notes[0]) {
    lines.push(`TLS: ${String(tls.legacy_protocol_notes[0]).trim()}`);
  }
  const exp = tls?.cert_expiry_warnings;
  if (Array.isArray(exp) && exp.length) {
    const w = exp[0];
    const subj = w?.subject ? String(w.subject).slice(0, 80) : '';
    lines.push(
      `Certificate: ~${w?.days_left != null ? `${w.days_left}d` : 'soon'} remaining${subj ? ` â€” ${subj}` : ''}`
    );
  }
  if (!lines.length) return baseTit;
  return truncatePdfPlainText(`${baseTit}\n${lines.join('\n')}`, 620);
}

function mapIntelResultToPdfRow(res, emailReport, urlLineChars) {
  const baseSrc = sourceLabelFromResult(res);
  let baseTit = sanitizePdfIntelTitle(pdfPrimaryTitle(res));
  baseTit = appendCensysPdfDetailLines(res, baseTit);
  if (emailReport && res.source === 'hibp') {
    const t = res.details?.title || res.details?.name;
    if (t && String(t).trim()) baseTit = sanitizePdfIntelTitle(String(t).trim());
    else if (res.platform && String(res.platform).trim()) baseTit = sanitizePdfIntelTitle(String(res.platform).trim());
    else baseTit = 'Have I Been Pwned breach';
  }
  const gaia =
    emailReport && (res.source === 'ghunt' || res.ui_bucket === 'ghunt_footprint')
      ? extractGaiaIdFromResult(res)
      : '';
  const googleAcctInfo = emailReport && isGhuntGoogleAccountInfoRow(res);
  if (gaia && !googleAcctInfo) {
    baseTit = appendGoogleUserIdToTitle(baseTit || String(res.platform || 'Google').trim(), gaia);
  }
  if (emailReport && res.ui_bucket === 'ghunt_footprint' && gaia) {
    const links = [
      res.maps_url || res.details?.maps_url,
      res.calendar_url || res.details?.calendar_url,
      res.wayback_url || res.wayback_plus_url || res.details?.wayback_url,
    ].filter((u) => u && String(u).startsWith('http'));
    if (links.length) {
      baseTit = appendGoogleUserIdToTitle(
        `${baseTit || 'Google Footprint'}\n${links.join('\n')}`,
        gaia,
      );
    }
  }
  let tit = formatPdfTitleBlock(res, baseTit);
  const pdfStealer = emailReport && res.source === 'hibp' && isStealerMalwareHibpRow(res);
  if (pdfStealer) tit = `[MALWARE_EXPOSURE] ${tit}`;
  const src = formatPdfSourceCell(res, baseSrc);
  let resolved = resolvePdfUrlForRow(res, baseTit, res.url);
  const domRaw = String(res.domain || '').trim();
  const noUsefulUrl = !normalizeUrl(resolved) || resolved === 'â€”';
  if (res.ui_bucket === 'threat_intelligence' && domRaw && noUsefulUrl) {
    resolved = `https://www.virustotal.com/gui/domain/${encodeURIComponent(domRaw)}`;
  } else if (res.ui_bucket === 'cyber_trust_score' && domRaw && noUsefulUrl) {
    resolved = /^https?:\/\//i.test(domRaw) ? domRaw : `https://${domRaw}`;
  }
  let u = resolved || 'â€”';
  if (emailReport && (res.source === 'ghunt' || res.ui_bucket === 'ghunt_footprint')) {
    if (gaia) {
      u = res.maps_url || res.details?.maps_url || `https://www.google.com/maps/contrib/${gaia}/reviews`;
    } else if (isLongGoogleCdnUrl(u)) {
      u = 'Google profile media (CDN URL omitted in PDF for readability)';
    }
  }
  const wrapChars = urlLineChars ?? (emailReport ? 56 : 50);
  const url = emailReport ? String(u).trim() : breakUrlForPdfTableCell(u, wrapChars);

  const row = { source: src, title: tit, url, pdfStealer };
  if (emailReport) {
    row.googleGaiaID =
      googleAcctInfo && gaia ? formatGoogleGaiaPdfCell(gaia, res.details?.name) : 'â€”';
  }
  return row;
}

function buildEmailModuleStatusRows(results) {
  const list = Array.isArray(results) ? results : [];
  const count = (s) => list.filter((r) => r && r.source === s).length;

  const out = [];
  const ghuntOk = list.some((r) => r.source === 'ghunt');
  out.push(['GHunt', ghuntOk ? 'OK â€” Google footprint captured' : 'No GHunt rows in export']);

  const hibpN = count('hibp');
  out.push([
    'HIBP / Profile recon',
    hibpN ? `OK â€” ${hibpN} breach record(s)` : 'No HIBP rows in export',
  ]);

  const vt = list.find((r) => r.ui_bucket === 'threat_intelligence');
  out.push([
    'VirusTotal',
    vt
      ? `OK â€” ${vt.malicious ?? 0} malicious Â· ${vt.suspicious ?? 0} suspicious`
      : 'Not run or not in export',
  ]);

  const cyber = list.find((r) => r.ui_bucket === 'cyber_trust_score');
  out.push([
    'Domain security audit',
    cyber
      ? `OK â€” risk ${cyber.risk_score ?? 'â€”'}/100 Â· DMARC ${cyber.dmarc_secure ? 'on' : 'weak'}`
      : 'Not run (typical for free-mail domains)',
  ]);

  const bb = count('blackbird');
  out.push(['Blackbird', bb ? `OK â€” ${bb} profile URL(s)` : 'No Blackbird rows']);

  out.push(['Holehe', count('holehe') ? `OK â€” ${count('holehe')} hit(s)` : 'No Holehe rows']);
  out.push([
    'SocialScan',
    count('socialscan') ? `OK â€” ${count('socialscan')} hit(s)` : 'No SocialScan rows',
  ]);
  out.push([
    'Google dork',
    list.some((r) => r.source === 'google_dork') ? 'OK â€” mentions captured' : 'No dork rows',
  ]);
  out.push([
    'GitHub (commits)',
    list.some((r) => r.source === 'github') ? 'OK â€” author match' : 'No GitHub rows',
  ]);
  return out;
}

/** Elevated infra / breach-driven follow-ups for Email PDF. */
function shouldEmailImmediateActions(results) {
  const list = Array.isArray(results) ? results : [];
  const hibp = list.filter((r) => r.source === 'hibp');
  if (hibpRowsStealerRisk(hibp)) return true;
  const vt = list.find((r) => r.ui_bucket === 'threat_intelligence');
  if (vt && Number(vt.malicious) > 0) return true;
  const cyber = list.find((r) => r.ui_bucket === 'cyber_trust_score');
  const rs = Number(cyber?.risk_score);
  if (Number.isFinite(rs) && rs >= 40) return true;
  return false;
}

function drawEmailImmediateActionsSection(doc, startY, pageW) {
  let y = startY + SECTION_GAP_MM * 0.85;
  doc.setFont(PDF_BODY_FONT, 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COL_ALERT);
  doc.text('Immediate Actions', MARGIN, y);
  y += 6;
  doc.setFont(PDF_BODY_FONT, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  const items = [
    'Enable 2FA on this mailbox and all high-value accounts that reuse it.',
    'Monitor dark web / breach dumps for cleartext passwords tied to this identity.',
    'Review and revoke unnecessary authorized OAuth apps for this email.',
  ];
  for (const t of items) {
    const lines = doc.splitTextToSize(`â€¢ ${t}`, pageW - 2 * MARGIN);
    doc.text(lines, MARGIN, y);
    y += lines.length * 4.6 + 2;
  }
  doc.setTextColor(0, 0, 0);
  return y + 6;
}

function drawEmailHibpTierTable(doc, title, rows, startY, colWidths, variant = 'high') {
  if (!rows || !rows.length) return startY;
  let y = startY + 6;
  doc.setFont(PDF_BODY_FONT, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(title, MARGIN, y);
  y += 4;
  const headFill =
    variant === 'high'
      ? [127, 29, 29]
      : [51, 65, 85];
  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    rowPageBreak: 'avoid',
    columns: [
      { header: 'Source', dataKey: 'source' },
      { header: 'Title', dataKey: 'title' },
      { header: 'URL', dataKey: 'url' },
    ],
    body: rows,
    styles: {
      font: PDF_BODY_FONT,
      fontSize: 8,
      cellPadding: { top: 4.0, right: 3.4, bottom: 4.8, left: 3.4 },
      overflow: 'linebreak',
      halign: 'left',
      valign: 'top',
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
      lineHeightFactor: 1.35,
      minCellHeight: 12,
    },
    headStyles: { fillColor: headFill, textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      source: {
        cellWidth: colWidths.source,
        overflow: 'linebreak',
        halign: 'left',
        fontSize: 8,
        lineHeightFactor: 1.4,
      },
      title: {
        cellWidth: colWidths.title,
        overflow: 'linebreak',
        halign: 'left',
        fontSize: 9.5,
        lineHeightFactor: 1.35,
      },
      url: { cellWidth: colWidths.url, overflow: 'ellipsize', halign: 'left', fontSize: 7 },
    },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      if (data.row?.raw?.pdfStealer) {
        data.cell.styles.textColor = COL_ALERT;
        data.cell.styles.fontStyle = 'bold';
      } else if (data.column.dataKey === 'url') {
        const t = String(data.cell.raw ?? '')
          .replace(/\u200b/g, '')
          .replace(/\s+/g, '')
          .replace(/\n/g, '');
        if (/^https?:\/\//i.test(t)) data.cell.styles.textColor = COL_BLUE;
      }
    },
    didDrawCell: (hook) => {
      const { doc: d, cell, column, section } = hook;
      if (section !== 'body' || column.dataKey !== 'url') return;
      const raw = cell.raw;
      const href =
        typeof raw === 'string'
          ? raw
              .trim()
              .replace(/\u200b/g, '')
              .replace(/\s+/g, '')
              .replace(/\n/g, '')
          : '';
      if (/^https?:\/\//i.test(href)) {
        d.link(cell.x, cell.y, cell.width, cell.height, { url: href });
      }
    },
  });
  const fin = doc.lastAutoTable?.finalY != null ? doc.lastAutoTable.finalY : y + 20;
  return fin + SECTION_GAP_MM * 0.5;
}

/**
 * Executive summary rows for Email Analysis PDF only (does not change phone flow).
 */
function buildEmailExecutiveSummaryRows(results, target) {
  const list = Array.isArray(results) ? results : [];
  const rows = [];
  const hibpBreaches = list.filter((r) => r && r.source === 'hibp');

  if (hibpBreaches.length === 0) {
    rows.push({
      label: 'Data Breach Status',
      body:
        'CLEAN - No Have I Been Pwned breach records were returned for this email in this export.',
      indicator: COL_GREEN,
    });
  } else {
    rows.push({
      label: 'Data Breach Status',
      body: `ALERT - ${hibpBreaches.length} Have I Been Pwned breach record(s) for this email.`,
      indicator: [239, 68, 68],
    });
  }

  if (hibpRowsStealerRisk(hibpBreaches)) {
    rows.push({
      label: 'Password Risk Assessment',
      body:
        'HIGH RISK â€” Stealer log breach exposure detected. Credentials associated with this email may have been harvested from malware-infected devices (active password compromise risk).',
      indicator: COL_ALERT,
      bodyColorAlert: true,
      note: 'Recommendation: change passwords for this email and linked accounts, use unique passwords per service, and enable multi-factor authentication.',
    });
  }

  const conf = computeEmailConfidenceScore(list);
  rows.push({
    label: 'Confidence Score',
    body: `${conf.level} â€” ${conf.body}`,
    indicator: conf.level === 'HIGH' ? COL_GREEN : conf.level === 'LOW' ? COL_SLATE : COL_AMBER,
  });

  const cyber = list.find((r) => r && r.ui_bucket === 'cyber_trust_score');
  const vt = list.find((r) => r && r.ui_bucket === 'threat_intelligence');
  if (cyber || vt) {
    const parts = [];
    if (cyber) {
      const dmarc = cyber.dmarc_secure ? 'DMARC enforced (reject/quarantine)' : 'DMARC weak or missing';
      parts.push(`Domain security audit: ${dmarc}; domain age ~${cyber.age_days ?? 'â€”'} days`);
    }
    if (vt) {
      parts.push(
        `Threat Intelligence (VirusTotal): malicious=${vt.malicious ?? 0}, suspicious=${vt.suspicious ?? 0}`
      );
    }
    rows.push({
      label: 'Network & Infrastructure',
      body: `VERIFIED â€” ${parts.join(' | ')}`,
      indicator: COL_BLUE,
    });
  } else {
    rows.push({
      label: 'Network & Infrastructure',
      body:
        'Domain security / VirusTotal blocks not present in this export (typical for consumer mail providers).',
      indicator: COL_SLATE,
    });
  }

  const identityName = extractEmailIdentityCandidate(list, target);
  if (identityName) {
    rows.push({
      label: 'Identity Reputation',
      body: `PROFILE - ${identityName}`,
      indicator: COL_GREEN,
    });
  } else {
    rows.push({
      label: 'Identity Reputation',
      body:
        'No strong public name extracted from GHunt, GitHub, procurement (SEAP) mentions, or email handle in this export.',
      indicator: COL_SLATE,
    });
  }

  return rows;
}

/**
 * Executive summary rows for phone lookup PDF (unchanged behaviour).
 * @returns {Array<{ label: string, body: string, indicator: number[] }>}
 */
function emailNameCandidate(extractedLeads) {
  const first = Array.isArray(extractedLeads?.emails) ? String(extractedLeads.emails[0] || '').trim() : '';
  if (!first || !first.includes('@')) return '';
  const local = first.split('@')[0] || '';
  if (!local) return '';
  const tokenized = local
    .replace(/[0-9]+/g, ' ')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!tokenized) return '';
  const words = tokenized
    .split(' ')
    .map((w) => w.trim())
    .filter((w) => w.length >= 2 && !/^(admin|office|contact|info|support|sales|noreply)$/i.test(w));
  if (!words.length) return '';
  return words.slice(0, 3).map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

function buildPhoneExecutiveSummaryRows(results, target, extractedLeads) {
  const list = Array.isArray(results) ? results : [];
  const hibp = list.find((r) => r && r.source === 'hibp_status');
  const meta = list.find((r) => r && r.source === 'phone_metadata');
  const phoneId = list.find((r) => r && r.type === 'phone_identity');

  const rows = [];

  if (hibp?.status === 'clean') {
    rows.push({
      label: 'Data Breach Status',
      body: `CLEAN - ${hibp.message || 'No breach has been detected on this number'}`,
      indicator: COL_GREEN,
    });
  } else if (hibp?.status === 'breached') {
    rows.push({
      label: 'Data Breach Status',
      body: `ALERT - ${hibp.message || 'Correlated breach record(s) present'}`,
      indicator: [239, 68, 68],
    });
  } else {
    rows.push({
      label: 'Data Breach Status',
      body: 'Not assessed in this export',
      indicator: COL_SLATE,
    });
  }

  let carrier = '';
  if (meta?.details && typeof meta.details.carrier === 'string' && meta.details.carrier.trim()) {
    carrier = meta.details.carrier.trim();
  } else if (Array.isArray(meta?.matched_tags)) {
    const prov = meta.matched_tags.find((t) => String(t).toLowerCase().includes('provider:'));
    if (prov) carrier = String(prov).replace(/^provider:\s*/i, '').trim();
  }
  const lineType = meta?.details?.line_type;
  const geoSuffix = lineType ? ` (${lineType})` : ' (Non-Geo)';
  if (carrier) {
    const inferred = inferCarrierFromPrefix(meta, target);
    const carrierDisplay = /^unknown$/i.test(carrier) && inferred ? `${carrier} (${inferred})` : carrier;
    rows.push({
      label: 'Network & Infrastructure',
      body: `VERIFIED - Carrier: ${carrierDisplay}${geoSuffix}`,
      indicator: COL_BLUE,
    });
  } else {
    rows.push({
      label: 'Network & Infrastructure',
      body: 'Carrier metadata not available in this export',
      indicator: COL_SLATE,
    });
  }

  const catalogRe = /catalog|pagini|liste|directory|white\s*pages|pagina/i;
  const blob = [
    phoneId?.source_display_name,
    phoneId?.platform,
    phoneId?.title,
    phoneId?.source,
    JSON.stringify(phoneId?.details || {}),
  ]
    .filter(Boolean)
    .join(' ');
  const looksCatalog = catalogRe.test(String(blob));

  const escalation = hasSecurityEscalation(list);
  if (escalation) {
    rows.push({
      label: 'Identity Reputation',
      body: '[!] CRITICAL RISK - SCAM/SPOOFING DETECTED',
      indicator: COL_ALERT,
      bodyColorAlert: true,
      note: 'Caution: This number is flagged in telemarketing and spoofing databases.',
    });
  } else if (phoneId && looksCatalog) {
    const cand = phoneId.details?.owner_name || phoneId.title || 'Catalog Web';
    rows.push({
      label: 'Identity Reputation',
      body: `RISK / ATTENTION - Identity Candidate: ${cand}`,
      indicator: COL_AMBER,
    });
  } else if (phoneId) {
    let name = phoneId.details?.owner_name || titleFromResult(phoneId);
    const genericIdentity = /phone identity signals|no clear owner|unknown/i.test(String(name || ''));
    if (genericIdentity) {
      const fromEmail = emailNameCandidate(extractedLeads);
      if (fromEmail) name = fromEmail;
    }
    rows.push({
      label: 'Identity Reputation',
      body: `PROFILE - ${name || 'Identity signals present'}`,
      indicator: COL_GREEN,
    });
  } else {
    rows.push({
      label: 'Identity Reputation',
      body: 'No structured identity block in this export',
      indicator: COL_SLATE,
    });
  }

  return rows;
}

function buildPersonExecutiveSummaryRows(results, target, extractedLeads) {
  const list = Array.isArray(results) ? results : [];
  const rows = [];
  const leads = normalizeExtractedLeads(extractedLeads);

  const amlHits = list.filter(
    (r) =>
      r &&
      (r.ui_bucket === 'compliance_alert' ||
        String(r.source || '').toLowerCase().includes('opensanctions') ||
        String(r.source || '').toLowerCase().includes('aml') ||
        /pep|sanction|wanted/i.test(String(r.title || '')) ||
        /pep|sanction|wanted/i.test(String(r.snippet || ''))),
  );
  if (amlHits.length === 0) {
    rows.push({
      label: 'AML / Sanctions Status',
      body: 'CLEAR — No PEP / sanctions / wanted hits in the local OpenSanctions-derived screen for this export.',
      indicator: COL_GREEN,
    });
  } else {
    const labels = amlHits
      .map((r) => String(r.title || r.source_display_name || r.source || 'Hit').trim())
      .filter(Boolean)
      .slice(0, 3);
    rows.push({
      label: 'AML / Sanctions Status',
      body: `ALERT — ${amlHits.length} compliance hit(s): ${labels.join('; ') || 'see Inspection Evidence'}.`,
      indicator: COL_ALERT,
    });
  }

  const primary =
    list.find(
      (r) =>
        r &&
        r.is_primary_identity === true &&
        !r.is_hidden &&
        r.ui_bucket !== 'compliance_alert' &&
        !String(r.source || '').toLowerCase().includes('opensanctions') &&
        !String(r.source || '').toLowerCase().includes('aml') &&
        !/sanctions?\s*compliance|pep\s*screen|aml\s*audit/i.test(String(r.title || '')),
    ) ||
    list.find(
      (r) =>
        r &&
        r.is_mention === false &&
        r.validation_status === 'passed' &&
        !r.is_hidden &&
        r.ui_bucket !== 'compliance_alert',
    );
  const affiliation =
    (Array.isArray(primary?.found_context) && primary.found_context[0]) ||
    primary?.details?.employer ||
    primary?.details?.affiliation ||
    '';
  const affiliationText = String(affiliation || '').trim();
  if (affiliationText || primary) {
    const host = (() => {
      try {
        return new URL(String(primary?.url || '')).hostname.replace(/^www\./, '');
      } catch {
        return '';
      }
    })();
    rows.push({
      label: 'Primary Affiliation',
      body: affiliationText
        ? `PROFILE — ${affiliationText}${host ? ` · ${host}` : ''}`
        : `PROFILE — ${String(primary?.title || target || 'Primary identity').slice(0, 120)}`,
      indicator: COL_BLUE,
    });
  } else {
    rows.push({
      label: 'Primary Affiliation',
      body: 'No primary affiliation resolved in this export.',
      indicator: COL_SLATE,
    });
  }

  const idParts = [];
  leads.emails.slice(0, 3).forEach((e) => idParts.push(`email ${e}`));
  leads.phones.slice(0, 3).forEach((p) => idParts.push(`phone ${p}`));
  leads.cuis.slice(0, 3).forEach((c) => idParts.push(`CUI ${c}`));
  if (!idParts.length) {
    list.forEach((r) => {
      if (!r || r.is_hidden) return;
      const dem = String(r.secondary_demotion_reason || '').toLowerCase();
      if (
        dem === 'divergent_location' ||
        dem === 'name_variation' ||
        dem === 'foreign_jurisdiction_directory'
      ) {
        return;
      }
      if (r.found_email) idParts.push(`email ${String(r.found_email).trim().toLowerCase()}`);
      if (r.found_phone) idParts.push(`phone ${r.found_phone}`);
    });
  }
  const uniqueIds = [...new Set(idParts.map((x) => String(x).toLowerCase()))].slice(0, 6);
  rows.push({
    label: 'Identified Identifiers',
    body: uniqueIds.length
      ? `VERIFIED — ${uniqueIds.join(' · ')}`
      : 'No email, phone, or CUI identifiers harvested from validated snippets in this export.',
    indicator: uniqueIds.length ? COL_GREEN : COL_SLATE,
  });

  return rows;
}

function buildExecutiveSummaryRows(results, target, extractedLeads, moduleName) {
  if (isEmailPdfModule(moduleName)) {
    return buildEmailExecutiveSummaryRows(results, target);
  }
  if (isPersonPdfModule(moduleName)) {
    return buildPersonExecutiveSummaryRows(results, target, extractedLeads);
  }
  return buildPhoneExecutiveSummaryRows(results, target, extractedLeads);
}

/**
 * @param {object} doc
 * @param {number} startY
 * @param {Array<{ label: string, body: string, indicator: number[], note?: string, bodyColorAlert?: boolean }>} rows
 * @returns {number} final Y (mm)
 */
function drawExecutiveSummaryBlock(doc, startY, rows) {
  let y = startY;
  doc.setTextColor(15, 23, 42);
  doc.setFont(PDF_BODY_FONT, 'bold');
  doc.setFontSize(11);
  doc.text('EXECUTIVE RISK SUMMARY', MARGIN, y);
  y += SECTION_GAP_MM;

  const innerW = PAGE_W_A4 - 2 * MARGIN - 8;
  rows.forEach((row) => {
    const ind = 3.8;
    doc.setFillColor(...row.indicator);
    doc.rect(MARGIN, y - 2.8, ind, ind, 'F');
    doc.setFont(PDF_BODY_FONT, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text(`${row.label}`, MARGIN + ind + 2, y);
    y += 5;
    doc.setFont(PDF_BODY_FONT, 'normal');
    doc.setFontSize(9);
    if (row.bodyColorAlert) {
      doc.setTextColor(...COL_ALERT);
      doc.setFont(PDF_BODY_FONT, 'bold');
    } else {
      doc.setTextColor(15, 23, 42);
    }
    const bodyLines = doc.splitTextToSize(row.body, innerW);
    doc.text(bodyLines, MARGIN + ind + 2, y);
    y += bodyLines.length * 4.1 + 2;
    doc.setTextColor(15, 23, 42);
    doc.setFont(PDF_BODY_FONT, 'normal');
    if (row.note) {
      doc.setFont(PDF_BODY_FONT, 'italic');
      doc.setFontSize(8);
      doc.setTextColor(...COL_ALERT);
      const noteLines = doc.splitTextToSize(row.note, innerW);
      doc.text(noteLines, MARGIN + ind + 2, y);
      y += noteLines.length * 3.6 + 4;
      doc.setTextColor(15, 23, 42);
      doc.setFont(PDF_BODY_FONT, 'normal');
      doc.setFontSize(9);
    }
    y += SECTION_GAP_MM * 0.55;
  });

  return y + 4;
}

function normalizeExtractedLeads(extractedLeads) {
  const emails = Array.isArray(extractedLeads?.emails)
    ? [
        ...new Set(
          extractedLeads.emails
            .map((x) => String(x || '').trim().toLowerCase())
            .filter(Boolean),
        ),
      ]
    : [];
  const phones = Array.isArray(extractedLeads?.phones)
    ? [...new Set(extractedLeads.phones.map((x) => String(x || '').trim()).filter(Boolean))]
    : [];
  const cuis = Array.isArray(extractedLeads?.cuis)
    ? [...new Set(extractedLeads.cuis.map((x) => String(x || '').trim()).filter(Boolean))]
    : [];
  const associatedUrls = Array.isArray(extractedLeads?.associated_urls)
    ? extractedLeads.associated_urls.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  return { emails, phones, cuis, associatedUrls };
}

function extractBrandsFromResults(results) {
  const out = [];
  const seen = new Set();
  const normalizeBrandToken = (v) =>
    String(v || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s&.-]/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  const generic = new Set([
    'contact',
    'home',
    'programari',
    'about',
    'telefon',
    'numar',
    'unknownphone',
    'listaabonatilor',
    'catalog web',
    'pagini albe',
    'telefonszam',
    'telefonszam:',
    'numar de telefon',
    'cine suna',
    'afla cine te suna',
    'articole curiozitati',
  ]);
  const genericNorm = new Set(Array.from(generic).map((x) => normalizeBrandToken(x)));
  const looksLikePhone = (text) => {
    const d = digitsOnly(text);
    return d.length >= 7;
  };
  const targets = new Set(
    (Array.isArray(results) ? results : [])
      .map((r) => String(r?.target || '').trim())
      .filter(Boolean)
      .flatMap((x) => {
        const d = digitsOnly(x);
        const out = [normalizeBrandToken(x)];
        if (d) {
          out.push(d);
          if (d.startsWith('40') && d.length >= 11) out.push(`0${d.slice(2)}`);
          if (d.startsWith('0') && d.length === 10) out.push(`40${d.slice(1)}`);
        }
        return out;
      })
  );
  const pick = (chunk) => {
    const t = String(chunk || '').replace(/\s+/g, ' ').trim();
    if (!t) return;
    if (looksLikePhone(t)) return;
    const normalized = normalizeBrandToken(t);
    if (!normalized || normalized.length < 3 || normalized.length > 42) return;
    if (targets.has(normalized) || targets.has(digitsOnly(normalized))) return;
    if (genericNorm.has(normalized)) return;
    if (!seen.has(normalized)) {
      seen.add(normalized);
      out.push(t);
    }
  };
  for (const r of Array.isArray(results) ? results : []) {
    if (!r || typeof r !== 'object') continue;
    if (r.type !== 'phone_mention') continue;
    const title = String(r.title || '').trim();
    if (!title) continue;
    const parts = title.split(/[-|â€¢|:|,]/).map((x) => x.trim()).filter(Boolean);
    if (parts.length) pick(parts[0]);
  }
  return out.slice(0, 6);
}

function collectDecisionTrace(results) {
  const list = Array.isArray(results) ? results : [];
  const row = list.find((r) => r && typeof r === 'object' && r.audit && Array.isArray(r.audit.reason_codes));
  const reasonCodes = Array.isArray(row?.audit?.reason_codes) ? row.audit.reason_codes.map((x) => String(x)) : [];
  const telemetry = row?.audit?.provider_telemetry || {};
  const providers = Array.isArray(telemetry.providers_used) ? telemetry.providers_used.map((x) => String(x)) : [];
  const fallbackUsed = Boolean(telemetry.fallback_used);
  const policyVersion = row?.audit?.policy_version ? String(row.audit.policy_version) : '';
  return { reasonCodes, providers, fallbackUsed, policyVersion };
}

/**
 * @param {object} data
 * @param {string} data.target
 * @param {Array<object>} data.results
 * @param {Array<{timestamp?:string,message?:string}|string>} data.logs
 * @param {{emails?: string[], associated_urls?: string[]}} [data.extractedLeads]
 * @param {string} data.moduleName
 * @param {string|Date} [data.scanDate] - optional scan timestamp
 * @param {boolean} [data.returnBlob] - if true, return PDF as Blob instead of triggering download
 */
export async function exportToPDF(data) {
  const {
    target = '',
    results = [],
    logs = [],
    extractedLeads,
    moduleName = 'Intelligence',
    scanDate,
    returnBlob = false,
  } = data || {};

  PDF_BODY_FONT = 'helvetica';
  PDF_MONO_FONT = 'courier';

  const when = scanDate ? new Date(scanDate) : new Date();
  const dateStr = when.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  if (typeof window !== 'undefined') {
    const ok = await embedNotoSansForPdf(doc);
    if (ok) {
      PDF_BODY_FONT = PDF_UNICODE_FAMILY;
      PDF_MONO_FONT = PDF_UNICODE_FAMILY;
    }
  }
  const pageH = doc.internal.pageSize.getHeight();
  const pageW = doc.internal.pageSize.getWidth();
  const emailReport = isEmailPdfModule(moduleName);
  const companyPdf = isCompanyPdfModule(moduleName);

  const headerBandMm = 38;
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, PAGE_W_A4, headerBandMm, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(PDF_BODY_FONT, 'bold');
  doc.setFontSize(16);
  doc.text('CyberTracer OSINT Core', MARGIN, 10.5);
  doc.setFont(PDF_BODY_FONT, 'normal');
  doc.setFontSize(9);
  doc.text('Enterprise Digital Due Diligence & Threat Assessment Workspace', MARGIN, 16.5);
  doc.setFontSize(10);
  doc.text(`Scan report · ${String(moduleName)}`, MARGIN, 23.5);
  doc.text(`Generated: ${dateStr}`, MARGIN, 30);
  doc.setTextColor(0, 0, 0);

  let y = headerBandMm + 8;
  doc.setFontSize(11);
  doc.setFont(PDF_BODY_FONT, 'bold');
  doc.text('Investigation target', MARGIN, y);
  y += 7;
  doc.setFont(PDF_BODY_FONT, 'normal');
  doc.setFontSize(10);
  const targetLines = doc.splitTextToSize(String(target || 'â€”'), PAGE_W_A4 - 2 * MARGIN);
  doc.text(targetLines, MARGIN, y);
  y += targetLines.length * 5 + SECTION_GAP_MM;

  const execRows = buildExecutiveSummaryRows(results, target, extractedLeads, moduleName);
  y = drawExecutiveSummaryBlock(doc, y, execRows);

  if (emailReport && shouldEmailImmediateActions(results)) {
    y = drawEmailImmediateActionsSection(doc, y, pageW);
  }

  y += SECTION_GAP_MM * 0.35;
  doc.setFont(PDF_BODY_FONT, 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Intelligence results', MARGIN, y);
  y += 6;

  const allList = Array.isArray(results) ? results : [];
  const hibpAll = emailReport ? allList.filter((r) => r && r.source === 'hibp') : [];
  const hibpHigh = emailReport ? hibpAll.filter((r) => hibpSecurityTier(r) === 'high') : [];
  const hibpLow = emailReport ? hibpAll.filter((r) => hibpSecurityTier(r) === 'low') : [];
  const intelRowsBase = emailReport
    ? allList.filter((r) => r && r.source !== 'hibp')
    : allList;

  const intelRowsFiltered = intelRowsBase.filter(shouldRenderIntelRowInPdf);
  const intelRowsForPdf = flattenPdfIntelRowsForTable(intelRowsFiltered).filter(shouldRenderIntelRowInPdf);

  const urlWrapChars = emailReport ? 58 : companyPdf ? 72 : 50;
  let tableRows = intelRowsForPdf.map((res) => mapIntelResultToPdfRow(res, emailReport, urlWrapChars));
  if (!tableRows.length && intelRowsBase.length > 0) {
    tableRows = [
      {
        source: 'Export filter',
        title: 'All intelligence rows were omitted as empty (no URL, score, or substantive text).',
        url: 'â€”',
        pdfStealer: false,
        ...(emailReport ? { googleGaiaID: 'â€”' } : {}),
      },
    ];
  }

  const tableContentW = pageW - 2 * MARGIN;

  const hibpColSourceW = emailReport ? 26 : 24;
  const hibpRestW = tableContentW - hibpColSourceW;
  const hibpColTitleW = emailReport ? hibpRestW * 0.42 : hibpRestW / 2;
  const hibpColUrlW = hibpRestW - hibpColTitleW;
  const hibpColPack = { source: hibpColSourceW, title: hibpColTitleW, url: hibpColUrlW };

  const colSourceW = Math.max(
    emailReport ? 22 : 24,
    Math.round(tableContentW * 0.22),
  );
  const colGaiaW = emailReport ? 52 : 0;
  const restIntel = tableContentW - colSourceW - (emailReport ? colGaiaW : 0);
  const colTitleW = emailReport ? restIntel * 0.44 : companyPdf ? restIntel * 0.32 : restIntel / 2;
  const colUrlW = emailReport ? restIntel - colTitleW : restIntel - colTitleW;

  const intelCellPad = emailReport
    ? { top: 4.5, right: 3.8, bottom: 5.2, left: 3.8 }
    : companyPdf
      ? { top: 3.4, right: 2.8, bottom: 4.0, left: 2.8 }
      : { top: 4.0, right: 3.2, bottom: 4.8, left: 3.2 };
  const titleFs = 9.5;
  const urlFs = emailReport ? 7 : 8;
  const gaiaFs = 8;
  const sourceFs = 8;

  const intelColumns = emailReport
    ? [
        { header: 'Source', dataKey: 'source' },
        { header: 'Title', dataKey: 'title' },
        { header: 'URL', dataKey: 'url' },
        { header: 'Google GaiaID', dataKey: 'googleGaiaID' },
      ]
    : [
        { header: 'Source', dataKey: 'source' },
        { header: 'Title', dataKey: 'title' },
        { header: 'URL', dataKey: 'url' },
      ];

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    rowPageBreak: 'avoid',
    columns: intelColumns,
    body: tableRows,
    styles: {
      font: PDF_BODY_FONT,
      fontSize: sourceFs,
      cellPadding: intelCellPad,
      overflow: 'linebreak',
      halign: 'left',
      valign: 'top',
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
      lineHeightFactor: 1.35,
      minCellHeight: 14,
    },
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: 255,
      fontStyle: 'bold',
      lineHeightFactor: 1.25,
    },
    columnStyles: {
      source: {
        cellWidth: colSourceW,
        overflow: 'linebreak',
        halign: 'left',
        fontSize: sourceFs,
        lineHeightFactor: 1.4,
        cellPadding: {
          top: intelCellPad.top,
          right: intelCellPad.right,
          bottom: intelCellPad.bottom + 0.8,
          left: intelCellPad.left,
        },
      },
      title: {
        cellWidth: colTitleW,
        overflow: 'linebreak',
        fontSize: titleFs,
        halign: 'left',
        lineHeightFactor: 1.35,
        ...(companyPdf
          ? {
              cellPadding: { top: 2.5, right: 1.9, bottom: 3.2, left: 1.9 },
            }
          : {}),
      },
      url: {
        cellWidth: colUrlW,
        overflow: emailReport ? 'ellipsize' : 'linebreak',
        halign: 'left',
        fontSize: urlFs,
        lineHeightFactor: 1.3,
        ...(companyPdf
          ? {
              cellPadding: { top: 2.6, right: 2.1, bottom: 3.2, left: 2.1 },
            }
          : {}),
      },
      ...(emailReport
        ? {
            googleGaiaID: {
              cellWidth: colGaiaW,
              overflow: 'linebreak',
              fontSize: gaiaFs,
              halign: 'left',
              valign: 'top',
              lineHeightFactor: 1.3,
            },
          }
        : {}),
    },
    ...(emailReport
      ? {
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            if (data.row?.raw?.pdfStealer) {
              data.cell.styles.textColor = COL_ALERT;
              data.cell.styles.fontStyle = 'bold';
            } else if (data.column.dataKey === 'url') {
              const t = String(data.cell.raw ?? '')
                .replace(/\u200b/g, '')
                .replace(/\s+/g, '')
                .replace(/\n/g, '');
              if (/^https?:\/\//i.test(t)) data.cell.styles.textColor = COL_BLUE;
            }
          },
        }
      : {}),
    didDrawCell: (hook) => {
      const { doc: d, cell, column, section } = hook;
      if (section !== 'body' || column.dataKey !== 'url') return;
      const raw = cell.raw;
      const href =
        typeof raw === 'string'
          ? raw
              .trim()
              .replace(/\u200b/g, '')
              .replace(/\s+/g, '')
              .replace(/\n/g, '')
          : '';
      if (/^https?:\/\//i.test(href)) {
        d.link(cell.x, cell.y, cell.width, cell.height, { url: href });
      }
    },
    willDrawCell: (hook) => {
      const { doc: d, column, section, cell } = hook;
      if (section !== 'body') return;
      const raw = String(cell.raw ?? '');
      if (column.dataKey === 'title') {
        if (
          /\[SCAM ALERT\]|\[SPOOFING\]|\[!\]/i.test(raw) ||
          /spoofing suspected|fraud reported/i.test(raw)
        ) {
          d.setTextColor(...COL_ALERT);
          d.setFont(PDF_BODY_FONT, 'bold');
        } else if (/\[SUSPICIOUS\]|telemarketing suspected/i.test(raw)) {
          d.setTextColor(...COL_AMBER);
          d.setFont(PDF_BODY_FONT, 'bold');
        } else if (/whatsapp:/i.test(raw)) {
          d.setTextColor(...COL_BLUE);
          d.setFont(PDF_BODY_FONT, 'bold');
        } else {
        d.setTextColor(0, 0, 0);
        d.setFont(PDF_BODY_FONT, 'normal');
        }
        d.setFontSize(titleFs);
        return;
      }
      if (column.dataKey === 'source') {
        d.setFontSize(sourceFs);
        d.setTextColor(0, 0, 0);
        d.setFont(PDF_BODY_FONT, 'normal');
        return;
      }
      if (column.dataKey === 'url') {
        d.setFontSize(urlFs);
        const urlCompact = raw.replace(/\u200b/g, '').replace(/\s+/g, '').replace(/\n/g, '');
        if (/^https?:\/\//i.test(urlCompact.trim())) {
          d.setTextColor(...COL_BLUE);
        } else {
          d.setTextColor(60, 60, 60);
        }
        d.setFont(PDF_BODY_FONT, 'normal');
        return;
      }
      if (column.dataKey === 'googleGaiaID') {
        d.setFontSize(gaiaFs);
        d.setTextColor(15, 23, 42);
        d.setFont(PDF_BODY_FONT, 'normal');
      }
    },
  });

  let afterTable =
    doc.lastAutoTable && doc.lastAutoTable.finalY != null ? doc.lastAutoTable.finalY : y + 40;

  if (emailReport) {
    const hiPdf = hibpHigh.map((r) => mapIntelResultToPdfRow(r, true, 64));
    const loPdf = hibpLow.map((r) => mapIntelResultToPdfRow(r, true, 64));
    afterTable = drawEmailHibpTierTable(
      doc,
      'Sensitive / High-Risk Breaches (security first)',
      hiPdf,
      afterTable,
      hibpColPack,
      'high'
    );
    afterTable = drawEmailHibpTierTable(
      doc,
      'Historical / Lower-Immediate-Risk Breaches',
      loPdf,
      afterTable,
      hibpColPack,
      'low'
    );

    const footprintRows = collectDigitalFootprintProfiles(allList);
    if (footprintRows.length > 0) {
      if (afterTable + 28 > pageH - MARGIN) {
        doc.addPage();
        afterTable = MARGIN + 4;
      }
      doc.setFont(PDF_BODY_FONT, 'bold');
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text('Digital Footprint Summary', MARGIN, afterTable + 6);
      autoTable(doc, {
        startY: afterTable + 11,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Platform', 'Profile URL']],
        body: footprintRows.map((x) => [x.label, String(x.url || '').trim()]),
        styles: {
          font: PDF_BODY_FONT,
          fontSize: 8,
          cellPadding: 2.6,
          overflow: 'linebreak',
          halign: 'left',
          valign: 'top',
          lineColor: [226, 232, 240],
        },
        headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: tableContentW - 32, overflow: 'ellipsize', fontSize: 7 },
        },
        didDrawCell: (hook) => {
          const { doc: d, cell, column, section } = hook;
          if (section !== 'body' || column.index !== 1) return;
          const raw = cell.raw;
          const href =
            typeof raw === 'string'
              ? raw
                  .trim()
                  .replace(/\u200b/g, '')
                  .replace(/\s+/g, '')
                  .replace(/\n/g, '')
              : '';
          if (/^https?:\/\//i.test(href)) {
            d.link(cell.x, cell.y, cell.width, cell.height, { url: href });
          }
        },
      });
      afterTable =
        doc.lastAutoTable && doc.lastAutoTable.finalY != null
          ? doc.lastAutoTable.finalY + SECTION_GAP_MM
          : afterTable + 24;
    }
  }

  let afterDiscoveryY = afterTable;

  afterDiscoveryY = drawInspectionEvidenceSection(
    doc,
    afterDiscoveryY + SECTION_GAP_MM * 0.5,
    intelRowsFiltered.length ? intelRowsFiltered : allList,
    pageW,
    pageH,
  );

  // VERIFIED LEADS — positioned after inspection evidence.
  const leads = normalizeExtractedLeads(extractedLeads);
  // Also fold contact fields from selected intelligence rows (person harvest).
  // Skip homonym / foreign-directory demotions so Aquila/Ploiesti noise stays out.
  (Array.isArray(results) ? results : []).forEach((r) => {
    if (!r || typeof r !== 'object') return;
    if (r.is_hidden) return;
    const dem = String(r.secondary_demotion_reason || '').toLowerCase();
    if (
      dem === 'divergent_location' ||
      dem === 'name_variation' ||
      dem === 'foreign_jurisdiction_directory'
    ) {
      return;
    }
    if (r.found_email) leads.emails.push(String(r.found_email).trim().toLowerCase());
    if (r.found_phone) leads.phones.push(String(r.found_phone).trim());
    const tags = Array.isArray(r.matched_tags) ? r.matched_tags : [];
    tags.forEach((t) => {
      const s = String(t || '');
      if (/^cui:/i.test(s)) leads.cuis.push(s.split(':').slice(1).join(':').trim());
    });
  });
  leads.emails = [...new Set(leads.emails.map((e) => String(e).trim().toLowerCase()).filter(Boolean))];
  leads.phones = [...new Set(leads.phones.filter(Boolean))];
  leads.cuis = [...new Set(leads.cuis.filter(Boolean))];
  const leadBrands = extractBrandsFromResults(results);
  const hasLeads =
    leads.emails.length > 0 ||
    leads.phones.length > 0 ||
    leads.cuis.length > 0 ||
    leads.associatedUrls.length > 0 ||
    leadBrands.length > 0;
  let leadsY = afterDiscoveryY + SECTION_GAP_MM;
  const leadsHeaderH = 6;
  const minBlockH = 20;
  if (leadsY + leadsHeaderH + minBlockH > pageH - MARGIN) {
    doc.addPage();
    leadsY = MARGIN + 4;
  }
  doc.setFont(PDF_BODY_FONT, 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('VERIFIED LEADS', MARGIN, leadsY);
  leadsY += 5.5;

  if (!hasLeads) {
    doc.setFont(PDF_BODY_FONT, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const emptyText = 'No additional contact leads discovered during deep web scan.';
    const emptyLines = doc.splitTextToSize(emptyText, pageW - 2 * MARGIN);
    doc.text(emptyLines, MARGIN, leadsY + 1);
    leadsY += emptyLines.length * 4.2 + 3;
  } else {
    const leadRows = [];
    leadBrands.forEach((b) => leadRows.push(['TYPE: BRAND', b]));
    leads.emails.forEach((e) => leadRows.push(['TYPE: EMAIL', e]));
    leads.phones.forEach((p) => leadRows.push(['TYPE: PHONE', p]));
    leads.cuis.forEach((c) => leadRows.push(['TYPE: CUI', c]));
    leads.associatedUrls.forEach((u) => leadRows.push(['TYPE: URL', u]));
    autoTable(doc, {
      startY: leadsY,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Type', 'Lead value']],
      body: leadRows,
      styles: {
        font: PDF_BODY_FONT,
        fontSize: 8,
        cellPadding: 2.4,
        overflow: 'linebreak',
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 'auto' },
      },
      didDrawCell: (hook) => {
        const { doc: d, cell, column, section } = hook;
        if (section !== 'body' || column.index !== 1) return;
        const raw = String(cell.raw || '').trim();
        if (/^https?:\/\//i.test(raw)) {
          d.link(cell.x, cell.y, cell.width, cell.height, { url: raw });
        }
      },
    });
    afterDiscoveryY =
      doc.lastAutoTable && doc.lastAutoTable.finalY != null ? doc.lastAutoTable.finalY : leadsY + 20;
    leadsY = afterDiscoveryY + 2;
  }

  doc.setFont(PDF_BODY_FONT, 'italic');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  const leadNote =
    'Note: These leads are cross-referenced from public web snippets and dorking results.';
  const leadNoteLines = doc.splitTextToSize(leadNote, pageW - 2 * MARGIN);
  const leadNoteH = leadNoteLines.length * 4;
  if (leadsY + leadNoteH > pageH - MARGIN) {
    doc.addPage();
    leadsY = MARGIN + 4;
  }
  doc.text(leadNoteLines, MARGIN, leadsY);
  afterDiscoveryY = leadsY + leadNoteH;

  const crossRefNote =
    'Note: This report includes automated cross-referencing from international databases (SE, NO, HU, RO).';
  const noteMaxW = pageW - 2 * MARGIN;
  doc.setFont(PDF_BODY_FONT, 'italic');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  const noteLines = doc.splitTextToSize(crossRefNote, noteMaxW);
  const noteLineH = 4;
  const noteBlockH = noteLines.length * noteLineH;

  let noteY = afterDiscoveryY + INTELLIGENCE_NOTE_GAP_MM;
  if (noteY + noteBlockH > pageH - MARGIN) {
    doc.addPage();
    noteY = MARGIN + 4;
  }
  doc.text(noteLines, MARGIN, noteY);

  // Decision Trace and Technical Audit Trail intentionally omitted from commercial PDF
  // (available only via Export Debug JSON).

  drawPdfFooterOnAllPages(doc, pageW, pageH);
  const filename = buildCyberTracerExportFilename({
    moduleName,
    target,
    extension: 'pdf',
  });
  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(filename);
}

function normalizeLogsForPdf(logs) {
  if (!Array.isArray(logs) || logs.length === 0) {
    return [['â€”', 'No log entries captured for this export.']];
  }
  return logs.map((entry) => {
    if (typeof entry === 'string') {
      const raw = cleanLogText(entry);
      const m = raw.match(/^(\d{2}:\d{2}:\d{2})\s+(.+)$/);
      if (m) return [m[1], m[2]];
      return ['â€”', raw];
    }
    const t = entry?.timestamp != null ? String(entry.timestamp) : '';
    let msg = entry?.message != null ? String(entry.message) : '';
    if (!msg.trim() && entry?.log_kind === 'confidence_breakdown' && entry?.confidence_breakdown) {
      const cb = entry.confidence_breakdown;
      const flow = String(cb.flow || '').trim();
      const stage = String(cb.stage || '').trim();
      const decision = String(cb.decision || '').trim();
      const reason = String(cb.reason || '').trim();
      if (flow) {
        msg = `[CONF:${flow}] ${stage} -> ${decision.toLowerCase()} (${reason})`;
      }
    }
    const m = cleanLogText(msg);
    return [t || 'â€”', m || 'â€”'];
  });
}

